'use client';

import { useState, useEffect } from 'react';
import type { WellKnownClientData } from '../well-known/types';
import { MicrofrontendConfigClient } from '../microfrontends-config/client';

let cachedServerClientConfigPromise: Promise<MicrofrontendConfigClient | null> | null =
  null;

async function fetchClientConfigFromServer(): Promise<MicrofrontendConfigClient | null> {
  try {
    const response = await fetch(
      '/.well-known/vercel/microfrontends/client-config',
    );
    if (response.status !== 200) {
      return null;
    }
    const responseJson = (await response.json()) as WellKnownClientData;
    return new MicrofrontendConfigClient(responseJson.config);
  } catch (err) {
    return null;
  }
}

/**
 * Hook to use the client microfrontends configuration. This hook will resolve
 * dynamic paths by fetching the configuration from the server if necessary,
 * allowing the server to specify the values for dynamic paths.
 */
export function useClientConfig(
  config: string | undefined,
  {
    removeFlaggedPathsFromDefault,
  }: {
    removeFlaggedPathsFromDefault?: boolean;
  } = {},
): {
  clientConfig: MicrofrontendConfigClient;
  isLoading: boolean;
} {
  const [clientConfig, setClientConfig] = useState<MicrofrontendConfigClient>(
    MicrofrontendConfigClient.fromEnv(config, {
      removeFlaggedPaths: removeFlaggedPathsFromDefault,
    }),
  );
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (
      process.env.NODE_ENV === 'test' &&
      process.env.MFE_FORCE_CLIENT_CONFIG_FROM_SERVER !== '1'
    ) {
      setIsLoading(false);
      return;
    }
    // Since we may remove flagged paths from the client config above, we need
    // to use the original client config to determine if the config has any
    // dynamic paths.
    const originalClientConfig = MicrofrontendConfigClient.fromEnv(config);
    // As an optimization, only fetch the config from the server if the
    // microfrontends configuration has any dynamic paths. If it doesn't,
    // then the server won't return any different values.
    const hasDynamicPaths = Object.values(
      originalClientConfig.applications,
    ).some((app) => app.routing?.some((group) => group.flag));
    if (!hasDynamicPaths) {
      setIsLoading(false);
      return;
    }
    if (!cachedServerClientConfigPromise) {
      cachedServerClientConfigPromise = fetchClientConfigFromServer();
    }
    void cachedServerClientConfigPromise
      .then((newConfig) => {
        if (newConfig) {
          setClientConfig((prevConfig) => {
            return prevConfig.isEqual(newConfig) ? prevConfig : newConfig;
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [config, clientConfig.applications]);

  return { clientConfig, isLoading };
}

export function resetCachedServerClientConfigPromise(): void {
  cachedServerClientConfigPromise = null;
}
